import React from "react";
import styled from "styled-components";
import { style, extractFlatColor, FlatColorMap } from "../style";

type Scale = "2xl" | "xl" | "lg" | "md" | "sm" | "xs";
type Weight = "base" | "bold" | "bolder" | "light";

type Props = React.PropsWithChildren<{
  /**
   * The element tag used in the final markup.
   */
  level: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";

  /**
   * The font size and line height of the heading. If unspecified or "auto",
   * falls back to the default for the heading level.
   *
   * @default "auto"
   */
  scale?: Scale | "auto";

  /**
   * The font weight of the heading. If unspecified or "auto",
   * falls back to the default for the scale.
   *
   * @default "auto"
   */
  weight?: Weight | "auto";

  /**
   * The color of the heading as a flat color value. If unspecified or "auto",
   * falls back to "grey.darkest".
   * @default "auto"
   */
  color?: FlatColorMap | "auto";

  /**
   * Testing utility tag.
   * @default "heading"
   */
  "data-testid"?: string;
}>;

const autoLevelScaleMapping = {
  h1: "2xl",
  h2: "xl",
  h3: "lg",
  h4: "md",
  h5: "xs",
  h6: "sm",
} as const;

const autoLevelWeightMapping = {
  "2xl": "bolder",
  xl: "bolder",
  lg: "bold",
  md: "bold",
  sm: "bold",
  xs: "bold",
} as const;

type BaseProps = {
  $scale: Scale;
  $weight: Weight;
  $color: FlatColorMap;
  $level?: Props["level"];
};

const Base = styled.h1<BaseProps>`
  &&& {
    all: unset;
    display: block;
    ${(props) => style.vars.headingFontRules[props.$scale]}
    ${(props) => ({ fontWeight: style.vars.fontWeight[props.$weight] })}
    ${(props) => ({ color: extractFlatColor(props.$color) })}
    ${(props) => ({ textTransform: props.$level === "h5" ? "uppercase" : undefined })}
  }
`;

export const Heading = (props: Props): JSX.Element => {
  const { scale = "auto", weight = "auto", color = "grey.darkest" } = props;
  const testId = props["data-testid"] ?? "heading";

  const $scale = scale === "auto" ? autoLevelScaleMapping[props.level] : scale;
  const $weight = weight === "auto" ? autoLevelWeightMapping[$scale] : weight;
  const $color = color === "auto" ? "grey.darkest" : color;

  return (
    <Base as={props.level} $level={props.level} $scale={$scale} $weight={$weight} $color={$color} data-testid={testId}>
      {props.children}
    </Base>
  );
};

Heading.levels = ["h1", "h2", "h3", "h4", "h5", "h6"] as const;
Heading.scales = ["auto", "2xl", "xl", "lg", "md", "sm", "xs"] as const;
Heading.weights = ["auto", "base", "bold", "bolder", "light"] as const;
